import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { PageHeader } from "../App";
import { 
  UploadSimple, 
  File, 
  FileJs, 
  FileText, 
  FileMd,
  CheckCircle,
  XCircle,
  Spinner,
  Trash
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import api from "../lib/api";

const getFileIcon = (filename) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'json':
      return FileJs;
    case 'md':
    case 'markdown':
      return FileMd;
    case 'txt':
      return FileText;
    default:
      return File;
  }
};

const FileItem = ({ file, status, result, onRemove }) => {
  const Icon = getFileIcon(file.name);
  
  return (
    <div 
      className="flex items-center gap-4 p-4 bg-zinc-900/50 border border-zinc-800 rounded-md"
      data-testid={`file-item-${file.name}`}
    >
      <div className="w-10 h-10 rounded bg-zinc-800 flex items-center justify-center flex-shrink-0">
        <Icon weight="regular" className="w-5 h-5 text-zinc-400" />
      </div>
      
      <div className="flex-1 min-w-0">
        <p className="text-sm text-zinc-200 truncate">{file.name}</p>
        <p className="text-xs text-zinc-500">
          {(file.size / 1024).toFixed(1)} KB
        </p>
        {result && (
          <p className="text-xs text-zinc-400 mt-1">
            {result.conversations_parsed} conversations parsed ({result.source_platform})
          </p>
        )}
      </div>
      
      <div className="flex items-center gap-2">
        {status === 'pending' && (
          <span className="text-xs text-zinc-500">Pending</span>
        )}
        {status === 'uploading' && (
          <Spinner weight="bold" className="w-5 h-5 text-blue-400 animate-spin" />
        )}
        {status === 'success' && (
          <CheckCircle weight="fill" className="w-5 h-5 text-emerald-400" />
        )}
        {status === 'error' && (
          <XCircle weight="fill" className="w-5 h-5 text-red-400" />
        )}
        
        {status !== 'uploading' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRemove(file.name)}
            data-testid={`remove-file-${file.name}`}
            className="text-zinc-500 hover:text-zinc-300"
          >
            <Trash weight="regular" className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default function Upload() {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      status: 'pending',
      result: null
    }));
    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/json': ['.json'],
      'text/plain': ['.txt'],
      'text/markdown': ['.md', '.markdown'],
    }
  });

  const removeFile = (filename) => {
    setFiles(prev => prev.filter(f => f.file.name !== filename));
  };

  const uploadAll = async () => {
    if (files.length === 0) return;
    
    setUploading(true);
    setProgress(0);
    
    let successCount = 0;
    let errorCount = 0;
    
    for (let i = 0; i < files.length; i++) {
      const fileItem = files[i];
      if (fileItem.status === 'success') continue;
      
      // Update status to uploading
      setFiles(prev => prev.map((f, idx) => 
        idx === i ? { ...f, status: 'uploading' } : f
      ));
      
      try {
        const result = await api.uploadDataset(fileItem.file);
        
        setFiles(prev => prev.map((f, idx) => 
          idx === i ? { ...f, status: 'success', result } : f
        ));
        successCount++;
      } catch (error) {
        setFiles(prev => prev.map((f, idx) => 
          idx === i ? { ...f, status: 'error' } : f
        ));
        errorCount++;
        console.error(`Failed to upload ${fileItem.file.name}:`, error);
      }
      
      setProgress(Math.round(((i + 1) / files.length) * 100));
    }
    
    setUploading(false);
    
    if (successCount > 0) {
      toast.success(`Successfully uploaded ${successCount} file${successCount > 1 ? 's' : ''}`);
    }
    if (errorCount > 0) {
      toast.error(`Failed to upload ${errorCount} file${errorCount > 1 ? 's' : ''}`);
    }
  };

  const clearCompleted = () => {
    setFiles(prev => prev.filter(f => f.status !== 'success'));
  };

  return (
    <div>
      <PageHeader 
        title="Upload Datasets" 
        subtitle="Import your AI chat exports for processing"
      />
      
      <div className="p-8 space-y-6">
        {/* Dropzone */}
        <div
          {...getRootProps()}
          className={`dropzone cursor-pointer ${isDragActive ? 'active' : ''}`}
          data-testid="file-dropzone"
        >
          <input {...getInputProps()} data-testid="file-input" />
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mb-4">
              <UploadSimple 
                weight={isDragActive ? "fill" : "duotone"} 
                className={`w-8 h-8 ${isDragActive ? 'text-blue-400' : 'text-zinc-400'}`} 
              />
            </div>
            <p className="text-zinc-300 mb-1">
              {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
            </p>
            <p className="text-sm text-zinc-500">
              or click to browse
            </p>
            <p className="text-xs text-zinc-600 mt-3">
              Supports: .json (ChatGPT, Perplexity, Gemini, Copilot exports), .md, .txt
            </p>
          </div>
        </div>

        {/* File List */}
        {files.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">
                Files ({files.length})
              </h3>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearCompleted}
                  disabled={!files.some(f => f.status === 'success')}
                  data-testid="clear-completed-btn"
                  className="text-zinc-400"
                >
                  Clear Completed
                </Button>
                <Button
                  size="sm"
                  onClick={uploadAll}
                  disabled={uploading || files.every(f => f.status === 'success')}
                  data-testid="upload-all-btn"
                >
                  {uploading ? (
                    <>
                      <Spinner weight="bold" className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <UploadSimple weight="bold" className="w-4 h-4 mr-2" />
                      Upload All
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            {uploading && (
              <Progress value={progress} className="h-1" />
            )}
            
            <div className="space-y-2">
              {files.map((item, idx) => (
                <FileItem
                  key={`${item.file.name}-${idx}`}
                  file={item.file}
                  status={item.status}
                  result={item.result}
                  onRemove={removeFile}
                />
              ))}
            </div>
          </div>
        )}

        {/* Supported Formats Info */}
        <div className="chart-container">
          <h3 className="text-sm font-medium text-zinc-400 mb-4 uppercase tracking-wider">
            Supported Formats
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: FileJs, name: 'ChatGPT', desc: 'Export from Settings > Data Controls' },
              { icon: FileJs, name: 'Perplexity', desc: 'JSON export from history' },
              { icon: FileJs, name: 'Gemini', desc: 'Google Takeout export' },
              { icon: FileMd, name: 'Markdown', desc: '.md files with User:/Assistant: format' },
            ].map(({ icon: Icon, name, desc }) => (
              <div key={name} className="flex items-start gap-3 p-3 bg-zinc-900/30 rounded-md">
                <Icon weight="duotone" className="w-5 h-5 text-zinc-400 mt-0.5" />
                <div>
                  <p className="text-sm text-zinc-200">{name}</p>
                  <p className="text-xs text-zinc-500">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
